import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { useAppDispatch } from '../../store';
import { trainingRunActions } from '../../store/entities/trainingRun';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { getFirstLastNameObj } from '../../utils/format';

interface Props {
  workers?: InvitedPersonDto | InvitedPersonDto[];
  questionnaireVersionId?: string;
  children?: ReactNode;
  disabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  sx?: Record<string, unknown>;
  isPlayground?: boolean;
}

export const TrainingRunButton: FC<Props> = ({ workers, questionnaireVersionId, children, disabled, variant = 'contained', sx, isPlayground }) => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { navigate, currentPath } = useNavigation();

  const handleClick = () => {
    dispatch(trainingRunActions.setLanguages([]));

    if (questionnaireVersionId) {
      navigate(
        PathKey.TrainingRun,
        { versionId: questionnaireVersionId },
        {
          questionnaireMode: 'true',
          ...(isPlayground && { playground: 'true' }),
          lang: i18n.language,
          backTo: currentPath,
        },
      );
      return;
    }

    if (!workers) return;

    const workerList = Array.isArray(workers) ? workers : [workers];
    const isGroup = workerList.length > 1;

    const trainingVersions = workerList.flatMap((w) => w.requiredTrainings ?? []).filter((v, i, arr) => arr.findIndex((a) => a.id === v.id) === i);

    if (trainingVersions.length === 0) return;

    dispatch(trainingRunActions.setPersons(workerList.map((w) => ({ id: w.employeeId!, name: getFirstLastNameObj(w) }))));

    dispatch(trainingRunActions.setVersions(trainingVersions.length > 1 ? trainingVersions : []));

    navigate(
      PathKey.TrainingRun,
      { versionId: trainingVersions[0].id! },
      {
        ...(isGroup && { group: 'true' }),
        ...(!isGroup && workerList[0].employeeId && { supplierEmployeeId: workerList[0].employeeId }),
        ...(isPlayground && { playground: 'true' }),
        lang: i18n.language,
        backTo: currentPath,
      },
    );
  };

  return (
    <QcsLoadingButton variant={variant} onClick={handleClick} disabled={disabled} sx={sx}>
      {children}
    </QcsLoadingButton>
  );
};
